/**
 * #1896 / F-N05 — owner-bound encrypted daily assessment drafts.
 *
 * Replaces plaintext localStorage persistence with offlineStore AES-GCM
 * envelopes (same F-04 strategy as survey offline store).
 */

import { offlineStore } from './store'

export type DailyAssessmentDraftV1 = {
  v: 1
  currentIdx: number
  note: string
  severity: 1 | 2 | 3
  responses: Array<[string, unknown]>
  photoEvidence: Array<[string, unknown]>
}

export function dailyAssessmentDraftId(facilityId: string, date = new Date()): string {
  return `daily-assessment-draft:${facilityId}:${date.toISOString().slice(0, 10)}`
}

/** Legacy plaintext localStorage key (pre-#1896). */
export function legacyDailyAssessmentLocalStorageKey(facilityId: string, date = new Date()): string {
  return dailyAssessmentDraftId(facilityId, date)
}

export function purgeLegacyLocalStorageDrafts(facilityId?: string): number {
  if (typeof window === 'undefined') return 0
  let n = 0
  const keys: string[] = []
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i)
    if (!key) continue
    if (!key.startsWith('daily-assessment-draft:')) continue
    if (facilityId && !key.startsWith(`daily-assessment-draft:${facilityId}:`)) continue
    keys.push(key)
  }
  for (const key of keys) {
    window.localStorage.removeItem(key)
    n++
  }
  return n
}

function isDraftShape(value: unknown): value is DailyAssessmentDraftV1 {
  if (!value || typeof value !== 'object') return false
  const v = value as Partial<DailyAssessmentDraftV1>
  if (!Array.isArray(v.responses) || !Array.isArray(v.photoEvidence)) return false
  return true
}

export async function loadDailyAssessmentDraft(
  facilityId: string,
): Promise<DailyAssessmentDraftV1 | null> {
  if (!facilityId) return null
  // Always drop legacy plaintext keys for this facility (shared-device residual)
  purgeLegacyLocalStorageDrafts(facilityId)
  if (!offlineStore.getActiveOwner()) {
    return null
  }
  try {
    const content = await offlineStore.getDraftContent(dailyAssessmentDraftId(facilityId))
    if (!isDraftShape(content)) return null
    return {
      v: 1,
      currentIdx: typeof content.currentIdx === 'number' ? content.currentIdx : 0,
      note: typeof content.note === 'string' ? content.note : '',
      severity:
        content.severity === 1 || content.severity === 2 || content.severity === 3
          ? content.severity
          : 2,
      responses: content.responses,
      photoEvidence: content.photoEvidence,
    }
  } catch (err) {
    if (err instanceof Error && err.message === 'offline_owner_mismatch') {
      // Adversarial: refuse restore under wrong identity
      return null
    }
    if (err instanceof Error && err.message === 'offline_owner_required') {
      return null
    }
    return null
  }
}

export async function saveDailyAssessmentDraft(
  facilityId: string,
  draft: Omit<DailyAssessmentDraftV1, 'v'> & { v?: 1 },
): Promise<void> {
  if (!facilityId) return
  if (!offlineStore.getActiveOwner()) {
    throw new Error('offline_owner_required')
  }
  const payload: DailyAssessmentDraftV1 = {
    v: 1,
    currentIdx: draft.currentIdx,
    note: draft.note,
    severity: draft.severity,
    responses: draft.responses,
    photoEvidence: draft.photoEvidence,
  }
  await offlineStore.saveDraft(dailyAssessmentDraftId(facilityId), payload)
  // Ensure no plaintext residual remains
  purgeLegacyLocalStorageDrafts(facilityId)
}

export async function clearDailyAssessmentDraft(facilityId: string): Promise<void> {
  if (!facilityId) return
  purgeLegacyLocalStorageDrafts(facilityId)
  try {
    await offlineStore.removeDraft(dailyAssessmentDraftId(facilityId))
  } catch {
    /* best-effort */
  }
}
